import axios, { AxiosError } from 'axios'
import React, { Dispatch, SetStateAction, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import { toast } from 'react-toastify'
import reportApiFailure from 'shared/api/reportApiFailure'
import { COUNTRY_CODE_PREFIX } from 'shared/ncts/common/classifier-util'
import useCodelist from 'features/declaration/codelist/api/useCodelist'
import FormRow from '../../../../components/FormRow'
import { getSelectValue } from '../../../../helpers'
import { apiService, handleResponseData } from '../../../../services/api.service'
import { ISelectOption } from '../../../../types/IClassifier'
import { IConsignmentDetail } from '../../../../types/IConsignmentDetail'
import { ExternalApiError, ValidationError } from '../../../../types/ValidationError'
import { blankNctsProfile, NctsProfileForm } from '../../hooks/useNctsProfile'
import { NctsProfileResponse } from '../../../../api/models'

interface ProfileModalParams {
  isProfileModalVisible: boolean
  toggleProfileModal: Dispatch<SetStateAction<boolean>>
  guaranteeTypes: Array<ISelectOption>
  authorityOffice: Array<ISelectOption>
  authorityCountry: Array<ISelectOption>
  nctsProfile: NctsProfileForm
  setNctsProfile: Dispatch<SetStateAction<NctsProfileForm | null>>
  setNctsProfileId: Dispatch<SetStateAction<number | null>>
  saveNctsProfile: (nctsProfile: NctsProfileForm) => Promise<NctsProfileResponse>
  isSharing: boolean | undefined
}

const EORI_REGEX = /^[A-Z]{2}[0-9]{1,15}$/
const SAFETY_AND_SECURITY_ID_EORI_PREFIXES = ['UA', 'RS']

function NctsProfileModal({
  isProfileModalVisible,
  toggleProfileModal,
  nctsProfile,
  setNctsProfile,
  setNctsProfileId,
  isSharing,
  guaranteeTypes,
  authorityOffice,
  authorityCountry,
  saveNctsProfile,
}: Readonly<ProfileModalParams>) {
  const commonCompanyPrefixes = ['Sabiedrība ar ierobežotu atbildību ', 'UŽDAROJI AKCINĖ BENDROVĖ ', 'Usługi Transportowe ']
  const [loading, setLoading] = useState<boolean>(false)
  const [eoriLoading, setEoriLoading] = useState<boolean>(false)
  const { t } = useTranslation()
  const [, countriesOptions] = useCodelist('COUNTRIES')

  const searchUsingEori = () => {
    setEoriLoading(true)
    if (nctsProfile.eori.length === 0) {
      toast.error(t('eori.error'))
      setTimeout(() => {
        setEoriLoading(false)
      }, 200)
      return
    }
    axios.get(
      apiService.getFullApiUrl('/eori'),
      {
        params: {
          eoriCode: nctsProfile.eori.replace(' ', ''),
        },
      },
    ).then(handleResponseData).then((eoriConsignmentDetails: IConsignmentDetail) => {
      setEoriLoading(false)
      let companyName = eoriConsignmentDetails.name

      const containsTypicalPrefix = commonCompanyPrefixes.find((prefix) => companyName.toLowerCase().startsWith(prefix.toLowerCase()))

      if (containsTypicalPrefix !== undefined) {
        companyName = companyName.substring(containsTypicalPrefix.length).replaceAll('"', '')
      }

      if (companyName.length > 35) {
        companyName = companyName.substring(0, 35)
        toast.warning('Company name was too long and has been shortened. Please check and verify the name.')
      }

      setNctsProfile({
        ...nctsProfile,
        companyName,
        companyCity: eoriConsignmentDetails.city,
        companyStreet: eoriConsignmentDetails.street,
        companyPostalCode: eoriConsignmentDetails.zip,
        companyCountry: eoriConsignmentDetails.country.substring(COUNTRY_CODE_PREFIX.length),
      })
    }).catch((error: AxiosError<ValidationError | ExternalApiError>) => {
      let errorCode = ''
      if (error.response && 'errorCode' in error.response.data) {
        errorCode = error.response.data.errorCode ?? '400'
      }

      toast.error(t('eori.error', { context: errorCode }), { autoClose: 3000 })
      setEoriLoading(false)
    })
  }

  const saveProfile = () => {
    setLoading(true)
    saveNctsProfile(nctsProfile)
      .then(() => {
        toggleProfileModal(false)
        setLoading(false)
        setNctsProfile(null)
        setNctsProfileId(null)
        toast.success(t('messages.savingSuccess'))
      })
      .catch((error) => {
        reportApiFailure(error, () => toast.error(t('messages.savingFailed')))
      })
      .finally(() => setLoading(false))

    // if (nctsProfile.id === 0 && !isSharing) {
    //   CustomsProfileService.postCustomsProfile(nctsProfile)
    //     .then(() => {
    //       toast.success(t('messages.savingSuccess'))
    //       setNctsProfile(blankNctsProfile)
    //       toggleProfileModal(false)
    //       setLoading(false)
    //     })
    //     .catch(() => {
    //       toast.error(t('messages.savingFailed'))
    //       setLoading(false)
    //     })
    //   setLoading(false)
    // } else if ((!isSharing && (nctsProfile.id !== 0)) || (isSharing && nctsProfile.id !== 0)) {
    //   CustomsProfileService.updateCustomsProfile(nctsProfile, nctsProfile?.id ?? 0)
    //     .then(() => {
    //       toast.success(t('messages.savingSuccess'))
    //       setNctsProfile(blankNctsProfile)
    //       setLoading(false)
    //       toggleProfileModal(false)
    //     })
    //     .catch(() => {
    //       toast.error(t('messages.savingFailed'))
    //     })
    //   setLoading(false)
    // }

    // if (isSharing && customerId !== undefined && nctsProfile.id === 0) {
    //   CustomsProfileService.approveAccess(customerId, nctsProfile.accessCode).then(() => {
    //     toast.success(t('messages.savingSuccess'))
    //     toggleProfileModal(false)
    //   }).catch(() => {
    //     toast.error(t('messages.savingFailed'))
    //     setLoading(false)
    //   })
    // }
  }
  const loadingSpinner = () => (<i className="fal fa-spinner fa-spin" />)

  return (
    <Modal
      show={isProfileModalVisible}
      className="overflow-hidden"
      dialogClassName="customs-modal modal"
      size="xl"
      aria-labelledby="contained-modal-title-vcenter"
      onHide={() => {
        setNctsProfile(blankNctsProfile)
        toggleProfileModal(false)
      }}
      animation={false}
      centered
    >
      <Modal.Header>
        <Modal.Title>
          {!isSharing ? (
            <h2
              className="text-capitalize"
            >
              {t('customsProfile.profile', { context: nctsProfile && nctsProfile.id !== 0 ? 'edit' : 'new' })}
            </h2>
          ) : (
            <h2
              className="text-capitalize"
            >
              {t('customsProfile.shareProfile')}
            </h2>
          )}

          <button
            type="button"
            className="btn btn-link no-underline btn-lg d-flex justify-content-center align-items-center px-0"
            onClick={() => {
              setNctsProfile(blankNctsProfile)
              toggleProfileModal(false)
            }}
          >
            <span>{t('buttons.close')}</span>
            <i className="fal fa-times fa-2x ms-2" />
          </button>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!isSharing && (
          <>
            <FormRow title={t('common.customsProfileName', { context: 'capitalized' })}>
              <input
                className="form-control"
                type="text"
                placeholder={t('common.fieldPlaceholder')}
                value={nctsProfile.name}
                onChange={(event) => {
                  setNctsProfile({
                    ...nctsProfile,
                    name: event.target.value,
                  })
                }}
              />
            </FormRow>
            <FormRow title={t('common.grnCode', { context: 'capitalized' })}>
              <input
                className="form-control"
                type="text"
                placeholder={t('common.fieldPlaceholder')}
                value={nctsProfile.grn}
                onChange={(event) => {
                  setNctsProfile({
                    ...nctsProfile,
                    grn: event.target.value,
                  })
                }}
              />
            </FormRow>
          </>
        )}
        <FormRow title={t('customers.accessCode', { context: 'capitalized' })}>
          <input
            className="form-control"
            type="text"
            placeholder={t('common.fieldPlaceholder')}
            value={nctsProfile.accessCode}
            onChange={(event) => {
              setNctsProfile({
                ...nctsProfile,
                accessCode: event.target.value,
              })
            }}
          />
        </FormRow>
        {!isSharing && (
          <>
            <FormRow title={t('common.guaranteeType', { context: 'capitalized' })}>
              <Select
                options={guaranteeTypes}
                className="select option mb-1"
                classNamePrefix="select"
                isClearable={false}
                menuPlacement="auto"
                placeholder={t('common.fieldPlaceholder')}
                value={getSelectValue(nctsProfile.guaranteeType, guaranteeTypes)}
                onChange={(option: ISelectOption | null) => {
                  if (option !== null) {
                    setNctsProfile({
                      ...nctsProfile,
                      guaranteeType: option.value,
                    })
                  }
                }}
              />
            </FormRow>
            <FormRow title={t('common.customsProfileAuthority', { context: 'capitalized' })}>
              <Select
                options={authorityOffice}
                className="select option mb-1"
                classNamePrefix="select"
                isClearable={false}
                menuPlacement="auto"
                placeholder={t('common.fieldPlaceholder')}
                value={getSelectValue(nctsProfile.authorityOffice, authorityOffice)}
                onChange={(option: ISelectOption | null) => {
                  if (option !== null) {
                    setNctsProfile({
                      ...nctsProfile,
                      authorityOffice: option.value,
                    })
                  }
                }}
              />
            </FormRow>
            <FormRow title={t('common.customsProfileCountry', { context: 'capitalized' })}>
              <Select
                options={authorityCountry}
                className="select option mb-1"
                classNamePrefix="select"
                isClearable={false}
                menuPlacement="auto"
                placeholder={t('common.fieldPlaceholder')}
                value={getSelectValue(nctsProfile.authorityCountry, authorityCountry)}
                onChange={(option: ISelectOption | null) => {
                  if (option !== null) {
                    setNctsProfile({
                      ...nctsProfile,
                      authorityCountry: option.value,
                    })
                  }
                }}
              />
            </FormRow>
            <FormRow title={t('declaration.eoriId', { context: 'capitalized' })}>
              <div className="input-group input-group-lg mb-2">
                <input
                  className="form-control"
                  type="text"
                  placeholder={t('common.fieldPlaceholder')}
                  value={nctsProfile.eori}
                  onChange={(event) => {
                    setNctsProfile({
                      ...nctsProfile,
                      eori: event.target.value,
                    })
                  }}
                />
                <div className="input-group-append">
                  <button
                    type="button"
                    className="btn btn-light h-100 border"
                    onClick={() => {
                      if (!eoriLoading) {
                        searchUsingEori()
                      }
                    }}
                  >
                    {
                      eoriLoading
                    && <i className="fa fa-spinner-third fa-spin" />
                    }
                    {
                      !eoriLoading
                    && <i className="fal fa-search" />
                    }
                  </button>
                </div>
              </div>
            </FormRow>
            {(!EORI_REGEX.test(nctsProfile.eori)
              || SAFETY_AND_SECURITY_ID_EORI_PREFIXES.some((prefix) => nctsProfile.eori.startsWith(prefix))) && (
              <>
                <FormRow title={t('common.safetyAndSecurityIdentificationNumber', { context: 'capitalized' })}>
                  <input
                    className="form-control"
                    type="text"
                    placeholder={t('common.fieldPlaceholder')}
                    value={nctsProfile.safetyAndSecurityIdentificationNumber}
                    onChange={(event) => {
                      setNctsProfile({
                        ...nctsProfile,
                        safetyAndSecurityIdentificationNumber: event.target.value,
                      })
                    }}
                  />
                </FormRow>
                <FormRow title={t('common.safetyAndSecurityCountry', { context: 'capitalized' })}>
                  <Select
                    options={authorityCountry}
                    className="select option mb-1"
                    classNamePrefix="select"
                    isClearable={false}
                    menuPlacement="auto"
                    placeholder={t('common.fieldPlaceholder')}
                    value={getSelectValue(nctsProfile.safetyAndSecurityCountry, authorityCountry)}
                    onChange={(option: ISelectOption | null) => {
                      if (option !== null) {
                        setNctsProfile({
                          ...nctsProfile,
                          safetyAndSecurityCountry: option.value,
                        })
                      }
                    }}
                  />
                </FormRow>
              </>
            )}

            <div className="mt-5">
              <div className="position-relative heading">
                <h3>{t('customsProfile.info')}</h3>
              </div>
              <FormRow
                title={t('customers.companyName', { context: 'capitalized' })}
                hasInfoButton
                tooltipContent={(
                  <>
                    {t('customers.companyNameExample')}
                  </>
                )}
              >
                <input
                  className="form-control"
                  type="text"
                  placeholder={t('common.fieldPlaceholder')}
                  value={nctsProfile.companyName}
                  onChange={(event) => {
                    setNctsProfile({
                      ...nctsProfile,
                      companyName: event.target.value,
                    })
                  }}
                />
              </FormRow>
              <FormRow
                title={t('customers.companyZipCode', { context: 'capitalized' })}
                hasInfoButton
                tooltipContent={(
                  <>
                    {t('customers.companyZipCodeExample')}
                  </>
                )}
              >
                <input
                  className="form-control"
                  type="text"
                  placeholder={t('common.fieldPlaceholder')}
                  value={nctsProfile.companyPostalCode}
                  onChange={(event) => {
                    setNctsProfile({
                      ...nctsProfile,
                      companyPostalCode: event.target.value,
                    })
                  }}
                />
              </FormRow>
              <FormRow
                title={t('customers.companyStreet', { context: 'capitalized' })}
                hasInfoButton
                tooltipContent={(
                  <>
                    {t('customers.streetExample')}
                  </>
                )}
              >
                <input
                  className="form-control"
                  type="text"
                  placeholder={t('common.fieldPlaceholder')}
                  value={nctsProfile.companyStreet}
                  onChange={(event) => {
                    setNctsProfile({
                      ...nctsProfile,
                      companyStreet: event.target.value,
                    })
                  }}
                />
              </FormRow>
              <FormRow
                title={t('declaration.p5.streetNumber', { context: 'capitalized' })}
              >
                <input
                  className="form-control"
                  type="text"
                  placeholder={t('common.fieldPlaceholder')}
                  value={nctsProfile.companyStreetNumber ?? ''}
                  onChange={(event) => {
                    setNctsProfile({
                      ...nctsProfile,
                      companyStreetNumber: event.target.value,
                    })
                  }}
                />
              </FormRow>
              <FormRow
                title={t('customers.companyCity', { context: 'capitalized' })}
                hasInfoButton
                tooltipContent={(
                  <>
                    {t('customers.companyCityExample')}
                  </>
                )}
              >
                <input
                  className="form-control"
                  type="text"
                  placeholder={t('common.fieldPlaceholder')}
                  value={nctsProfile.companyCity}
                  onChange={(event) => {
                    setNctsProfile({
                      ...nctsProfile,
                      companyCity: event.target.value,
                    })
                  }}
                />
              </FormRow>
              <FormRow title={t('customers.companyCountry', { context: 'capitalized' })}>
                <Select
                  options={countriesOptions}
                  className="select option mb-1"
                  classNamePrefix="select"
                  isClearable={false}
                  menuPlacement="auto"
                  placeholder={t('common.fieldPlaceholder')}
                  value={getSelectValue(COUNTRY_CODE_PREFIX + nctsProfile.companyCountry, countriesOptions)}
                  onChange={(option: ISelectOption | null) => {
                    if (option !== null) {
                      setNctsProfile({
                        ...nctsProfile,
                        companyCountry: option.value.substring(COUNTRY_CODE_PREFIX.length),
                      })
                    }
                  }}
                />
              </FormRow>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          className="btn btn-dark btn-lg m-0 text-primary"
          onClick={() => saveProfile()}
        >
          {
            loading && loadingSpinner()
          }
          {
            !loading && (
              <span>{t('buttons.save')}</span>
            )
          }
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export default NctsProfileModal
